import { DoorUnlockPolling, type DoorUnlockPhase } from "@components/Polling/DoorUnlockPolling";
import { preloadQuery } from "@relay";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { z } from "zod";
import { RafflePresent } from "~/assets/raffle/RafflePresent";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import type { resultUnlockRequestQuery } from "./__generated__/resultUnlockRequestQuery.graphql";

const searchSchema = z.object({
	winner: z.boolean(),
	unlockRequestId: z.string().uuid(),
});

const query = graphql`
	query resultUnlockRequestQuery($unlockRequestId: UUID!) {
		unlockRequest(id: $unlockRequestId) @required(action: THROW) {
			id
			status
			unlockedAt
			error
			reservation {
				id
				... on RaffleReservation {
					space {
						id
						position
						frame
						hub {
							id
							slug
							meta {
								id
								name
								street
							}
						}
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/raffle/$raffleId/result")({
	component: RouteComponent,
	validateSearch: searchSchema,
	beforeLoad: async (ctx) => {
		document.title = "bacehub | Startseite";
		return preloadQuery<resultUnlockRequestQuery>(ctx.context.relayEnvironment, query, {
			unlockRequestId: ctx.search.unlockRequestId,
		});
	},
	loader: async (ctx) => {
		return ctx.context;
	},
});

function RouteComponent() {
	const { winner, unlockRequestId } = Route.useSearch();
	const navigate = useNavigate();
	const data = usePreloadedQuery<resultUnlockRequestQuery>(query, Route.useLoaderData());
	const { unlockRequest } = data;
	const i18n = useI18nContext().LL["new-ui"];

	const space = unlockRequest.reservation && "space" in unlockRequest.reservation ? unlockRequest.reservation.space : null;

	const onSpaceClosed = () => {
		navigate({ to: "/$hubMetaName/raffle/$raffleId/thank-you", from: Route.fullPath });
	};

	const renderContent = (phase: DoorUnlockPhase) => {
		// Same layout for both winner and consolation - no Card
		return (
			<div className="flex flex-col items-center gap-4 px-6 max-w-sm mx-auto">
				{phase === "waiting_for_close" && (
					<>
						<div className="flex flex-col items-start self-stretch gap-2 py-4">
							<h2 className="text-2xl text-neutral-900">
								{winner ? <T>{i18n.RAFFLE_WIN_TITLE()}</T> : <T>{i18n.RAFFLE_CONSOLATION_TITLE()}</T>}
							</h2>
							<p className="text-sm text-muted-foreground">
								<T>{i18n.RAFFLE_COMPARTMENT_OPEN_DESC()}</T>
							</p>
						</div>
						<div className="flex justify-center items-center self-stretch py-6">
							<RafflePresent className="w-60 h-60" />
						</div>
					</>
				)}

				{phase === "waiting_for_open" && (
					<>
						<div className="flex flex-col items-start self-stretch gap-2 py-4">
							<h2 className="text-2xl text-neutral-900">
								{winner ? <T>{i18n.RAFFLE_OPENING_PRIZE()}</T> : <T>{i18n.RAFFLE_OPENING_CONSOLATION()}</T>}
							</h2>
							<p className="text-sm text-muted-foreground animate-pulse">
								<T>{i18n.RAFFLE_PLEASE_WAIT()}</T>
							</p>
						</div>
						<div className="flex justify-center items-center self-stretch py-6">
							<RafflePresent className="w-60 h-60" />
						</div>
					</>
				)}
			</div>
		);
	};

	return (
		<PageContainer>
			<AppHeader location={{ address: space?.hub?.meta?.street || "" }} service="RAFFLE" />
			<DoorUnlockPolling
				unlockRequestId={unlockRequestId}
				target="closed"
				onExit={() => navigate({ to: "/$hubMetaName", from: Route.fullPath })}
				onTerminate={(outcome) => {
					if (outcome.type === "closed") {
						onSpaceClosed();
						return;
					}
					if (outcome.type === "timed_out") toast.error(i18n.RAFFLE_COMPARTMENT_NOT_OPENED());
					else
						toast.error(i18n.RAFFLE_SOMETHING_WENT_WRONG(), {
							description: "message" in outcome ? outcome.message : undefined,
						});
					setTimeout(() => navigate({ to: "/$hubMetaName", from: Route.fullPath }), 3000);
				}}
			>
				{({ phase }) => <div className="px-6 mt-4">{renderContent(phase)}</div>}
			</DoorUnlockPolling>
		</PageContainer>
	);
}
